import { useMemo, useState } from 'react'
import { Button, EmptyState, Input } from '@orbind/ui'

/**
 * Adicionar pessoas ao grupo (pedido 2026-09-14): antes o botão despejava uma
 * lista gigante no meio dos dados do grupo. Agora abre uma aba própria com
 * busca (lupa) e as pessoas em ORDEM ALFABÉTICA, separadas pela letra inicial.
 * Dá para marcar várias e adicionar de uma vez.
 */

export type Candidato = { id: string; name: string; sub: string; foto: string | null }

/** Compara ignorando caixa e acento: "joao" acha "João". */
const normalizar = (t: string) => t.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()

export function AdicionarPessoas({
  candidatos,
  onVoltar,
  onAdicionar,
}: {
  candidatos: readonly Candidato[]
  onVoltar: () => void
  onAdicionar: (memberIds: string[]) => void
}) {
  const [busca, setBusca] = useState('')
  const [marcados, setMarcados] = useState<Set<string>>(new Set())

  const grupos = useMemo(() => {
    const termo = normalizar(busca.trim())
    const filtrados = [...candidatos]
      .filter((c) => termo === '' || normalizar(`${c.name} ${c.sub}`).includes(termo))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }))
    const porLetra = new Map<string, Candidato[]>()
    for (const c of filtrados) {
      const letra = normalizar(c.name.trim().charAt(0) || '#').toUpperCase()
      const chave = /[A-Z]/.test(letra) ? letra : '#'
      const lista = porLetra.get(chave) ?? []
      lista.push(c)
      porLetra.set(chave, lista)
    }
    return [...porLetra]
  }, [candidatos, busca])

  const alternar = (id: string) =>
    setMarcados((atual) => {
      const novo = new Set(atual)
      if (novo.has(id)) novo.delete(id)
      else novo.add(id)
      return novo
    })

  return (
    <div className="ch-acervo">
      <div className="ch-drawer__head">
        <button type="button" className="ch-drawer__back" aria-label="Voltar aos dados" onClick={onVoltar}>
          <i className="fas fa-arrow-left" aria-hidden />
        </button>
        <span className="ch-drawer__id">
          <span className="ch-drawer__title">Adicionar pessoas</span>
          <span className="ch-drawer__sub">
            {marcados.size === 0
              ? `${candidatos.length} ${candidatos.length === 1 ? 'pessoa disponível' : 'pessoas disponíveis'}`
              : `${marcados.size} ${marcados.size === 1 ? 'selecionada' : 'selecionadas'}`}
          </span>
        </span>
      </div>

      {/* Campo do @orbind/ui; a lupa só se sobrepõe ao começo dele. */}
      <div className="ch-pessoas__busca">
        <i className="fas fa-search ch-pessoas__lupa" aria-hidden />
        <Input
          value={busca}
          autoFocus
          placeholder="Pesquisar nome ou e-mail"
          aria-label="Pesquisar pessoa"
          onChange={(e) => setBusca(e.target.value)}
        />
        {busca ? (
          <button
            type="button"
            className="ch-iconbtn ch-pessoas__limpar"
            aria-label="Limpar pesquisa"
            onClick={() => setBusca('')}
          >
            <i className="fas fa-times" aria-hidden />
          </button>
        ) : null}
      </div>

      <div className="ch-acervo__corpo ch-pessoas__lista">
        {grupos.length === 0 ? (
          <div style={{ padding: '28px 8px' }}>
            <EmptyState
              icon="fas fa-user-slash"
              title={busca ? 'Ninguém encontrado' : 'Todos já estão no grupo'}
              description={
                busca ? `Nenhuma pessoa com “${busca}”. Confira a grafia.` : 'Não há mais ninguém para adicionar.'
              }
            />
          </div>
        ) : (
          grupos.map(([letra, pessoas]) => (
            <section key={letra} className="ch-acervo__mes">
              <span className="ch-acervo__rotulo">{letra}</span>
              {pessoas.map((p) => {
                const marcado = marcados.has(p.id)
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="checkbox"
                    aria-checked={marcado}
                    className={`ch-contact ch-pessoas__pessoa${marcado ? ' ch-pessoas__pessoa--marcada' : ''}`}
                    onClick={() => alternar(p.id)}
                  >
                    <span className="ch-contact__avatar">
                      {p.foto ? <img className="ch-avatar__img" src={p.foto} alt="" /> : iniciais(p.name)}
                    </span>
                    <span className="ch-contact__main">
                      <span className="ch-contact__name">{p.name}</span>
                      <span className="ch-contact__sub">{p.sub}</span>
                    </span>
                    <span className="ch-pessoas__marca" aria-hidden>
                      {marcado ? <i className="fas fa-check" /> : null}
                    </span>
                  </button>
                )
              })}
            </section>
          ))
        )}
      </div>

      <div className="ch-pessoas__rodape">
        <Button variant="ghost" onClick={onVoltar}>
          Cancelar
        </Button>
        <Button icon="fas fa-user-plus" disabled={marcados.size === 0} onClick={() => onAdicionar([...marcados])}>
          {marcados.size > 1 ? `Adicionar ${marcados.size} pessoas` : 'Adicionar'}
        </Button>
      </div>
    </div>
  )
}

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/)
  return ((partes[0]?.[0] ?? '') + (partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : '')).toUpperCase()
}
